import { HttpClient } from '@angular/common/http';
import * as ApiTransaction from '../interfaces/api/transaction';
import * as ApiPagination from '../interfaces/api/pagination';
import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { Tracking } from '../interfaces/api/shipment';
import { Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root',
})
export class TransactionService extends BaseService {
  private urlTransaction = `${this.apiUrl}/transaction`;

  constructor(private http: HttpClient) {
    super();
  }

  createTransaction(transaction: ApiTransaction.Transaction) {
    return this.http.post<ApiTransaction.ResponseBodyCreateTransaksi>(this.urlTransaction, transaction);
  }

  checkTransaction(transaction: ApiTransaction.CheckTransaction) {
    return this.http.post<ApiTransaction.ResponseBodyCheckTransaksi>(`${this.urlTransaction}/check`, transaction);
  }

  updateTransactionShipment(payload: { idTransaksi: number; data: ApiTransaction.PayloadUpdateShippingTransaction }) {
    return this.http.post<ApiTransaction.ResponseBodyTransaksiDetail>(
      `${this.urlTransaction}/update_shipping/${payload.idTransaksi}`,
      payload.data,
    );
  }

  updateBankPayment(payload: { idTransaksi: number; data: ApiTransaction.PayloadUpdateBankPayment }) {
    return this.http.post<ApiTransaction.ResponseBodyTransaksiDetail>(
      `${this.urlTransaction}/update_payment_bank/${payload.idTransaksi}`,
      payload.data,
    );
  }

  updatePaymentProof(payload: { idTransaksi: number; data: ApiTransaction.PayloadUpdatePaymentProof }) {
    return this.http.post<ApiTransaction.ResponseBodyTransaksiDetail>(
      `${this.urlTransaction}/update_payment_proof/${payload.idTransaksi}`,
      payload.data,
    );
  }

  requestFaktur(payload: { idTransaksi: number }) {
    return this.http.post<ApiTransaction.ResponseReqFaktur>(
      `${this.urlTransaction}/faktur_pajak/${payload.idTransaksi}`,
      {},
      {
        params: {
          no_loading: true,
        },
      },
    );
  }

  completeTransaction(payload: { idTransaksi: number }) {
    return this.http.post<{ message: string }>(
      `${this.urlTransaction}/complete_transaction/${payload.idTransaksi}`,
      {},
      {
        params: {
          no_loading: true,
        },
      },
    );
  }

  getTransactionList(queryParams: {
    page: number;
    per_page?: number;
    status?: 'ONGOING' | 'WAITING_PAYMENT' | 'COMPLETE';
    code?: string;
  }) {
    const url = new URL(`${this.urlTransaction}`);

    url.searchParams.set('page', queryParams?.page?.toString());

    if (queryParams?.per_page) {
      url.searchParams.set('perPage', queryParams?.per_page?.toString());
    }

    if (queryParams?.status) {
      url.searchParams.set('status', queryParams?.status?.toString());
    }

    if (queryParams?.code) {
      url.searchParams.set('code', queryParams?.code?.toString());
    }

    return this.http.get<ApiTransaction.ResponseBodyTransaction>(url.href);
  }

  getTransactionTaxList(queryParams: { status?: '0' | '1' | '2' | '3'; page?: number; per_page?: number }) {
    const url = new URL(`${this.apiUrl}/transaction_tax_request`);

    if (queryParams?.status) {
      url.searchParams.set('status', queryParams?.status);
    }

    if (queryParams?.page) {
      url.searchParams.set('page', queryParams?.page?.toString());
    }

    if (queryParams?.per_page) {
      url.searchParams.set('perPage', queryParams?.per_page?.toString());
    }

    return this.http.get<ApiTransaction.ResponseBodyTransaction>(url.href);
  }

  getTransactionInvoice(idTransaction: number) {
    return this.http.get(`${this.urlTransaction}/invoice/${idTransaction}`, { responseType: 'arraybuffer' });
  }

  getSingleTransaction(idTransaction: number) {
    return this.http.get<ApiTransaction.ResponseBodySingle>(`${this.urlTransaction}/${idTransaction}`);
  }

  getUnpaidTransactionCount() {
    return this.http.get<ApiTransaction.ResponseUnpaidTransactionCount>(`${this.urlTransaction}/count_unpaid`);
  }

  getTracking(idTdTransaction: number) {
    return this.http.get<Tracking>(`${this.apiUrl}/shipment/${idTdTransaction}/tracking`);
  }

  reviewTransaction(transactionId: number, payload: ApiTransaction.RequestBodyReviewTransaction) {
    return this.http.post<{ message: string }>(`${this.urlTransaction}/review_transaction/${transactionId}`, payload);
  }

  reviewProduct(idTdTransactionProduct: number, payload: ApiTransaction.RequestBodyReviewProduct) {
    return this.http.patch<ApiTransaction.ResponseBodyReviewProduct>(
      `${this.urlTransaction}/review/${idTdTransactionProduct}`,
      payload,
    );
  }

  getBankList() {
    return this.http.get<ApiTransaction.GetRequestBankList>(`${this.apiUrl}/bank`);
  }

  changePayment(transactionId: number, newPayment: string) {
    return this.http.patch<ApiTransaction.ResponseBodyChangePayment>(
      `${this.urlTransaction}/change-payment/${transactionId}`,
      { payment_method: newPayment },
    );
  }

  renewTransactionMidtrans(transactionId: number) {
    return this.http.patch<{ message: string }>(`${this.urlTransaction}/renewPayment/${transactionId}`, {});
  }

  uploadPaymentProof(transactionId: number, formData: FormData) {
    return this.http.post<ApiTransaction.ResponseBodyTransaction>(
      `${this.urlTransaction}/manual_transfer/${transactionId}?_method=patch`,
      formData,
    );
  }

  getCurrentTier() {
    return this.http.get<ApiTransaction.ResponseBodyCurrentTier>(`${this.apiUrl}/tier/current`, {
      params: {
        no_loading: true,
      },
    });
  }

  cancelTransaction(idTdTransactionProduct: number) {
    return this.http.patch<ApiTransaction.ResponseBodyReviewProduct>(
      `${this.urlTransaction}/cancel/${idTdTransactionProduct}`,
      {},
    );
  }

  cancelTransactionWithReason(transactionId: number, cancelReason: string) {
    const reason = { cancel_reason: cancelReason };

    return this.http.post(`${this.urlTransaction}/cancel/${transactionId}`, reason);
  }

  refundTransactionWithReason(transactionId: number, refundReason: string) {
    const reason = { refund_reason: refundReason };

    return this.http.post(`${this.urlTransaction}/refund/${transactionId}`, reason);
  }

  cancelPayment(idTransaction: number) {
    return this.http.patch<ApiTransaction.ResponseBodyCancelPayment>(
      `${this.urlTransaction}/cancel-payment/${idTransaction}`,
      {},
    );
  }

  downloadInvoice(url: string) {
    return this.http.get(url, { responseType: 'blob' });
  }
}
